<template>
  <Modal :open="open" size="big" @close="$emit('close')">
    <div>
      <h1 class="text-2xl font-bold">{{ t("title") }}</h1>
      <p v-if="analyzedAt" class="mt-1 text-xs text-gray-500">Analysée le {{ formattedAnalyzedAt }}</p>
    </div>

    <div v-if="errorMessage" class="mt-6 rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
      <p class="font-semibold">Analyse indisponible</p>
      <p class="mt-1">{{ errorMessage }}</p>
    </div>

    <div
      v-else-if="!analyzedAt"
      class="mt-6 rounded-md border-l-4 border-gray-300 bg-gray-50 p-4 text-sm text-gray-700"
    >
      {{ t("not_analyzed") }}
    </div>

    <div v-else class="mt-6 space-y-6">
      <div class="flex items-center gap-3">
        <span class="text-sm font-semibold text-gray-700">Verdict global :</span>
        <span :class="verdictBadgeClass" class="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold">
          {{ verdictLabel(verdict) }}
        </span>
      </div>

      <div v-if="extracted">
        <h2 class="text-sm font-semibold text-gray-800">Données extraites</h2>
        <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div v-for="(value, key) in extractedFields" :key="key" class="flex gap-2 text-sm">
            <dt class="font-medium text-gray-500">{{ key }} :</dt>
            <dd class="text-gray-800">{{ value || "—" }}</dd>
          </div>
        </dl>
      </div>

      <div v-if="checks.length > 0">
        <h2 class="text-sm font-semibold text-gray-800">Vérifications par champ</h2>
        <table class="mt-2 w-full text-left text-sm">
          <thead class="border-b border-gray-200 text-xs uppercase text-gray-500">
            <tr>
              <th class="py-2 pr-4">Champ</th>
              <th class="py-2 pr-4">Verdict</th>
              <th class="py-2">Raison</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="check in checks" :key="check.field" class="border-b border-gray-100">
              <td class="py-2 pr-4 font-medium text-gray-700">{{ check.label }}</td>
              <td class="py-2 pr-4">
                <span
                  :class="badgeClassFor(check.verdict)"
                  class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold"
                >
                  {{ verdictLabel(check.verdict) }}
                </span>
              </td>
              <td class="py-2 text-gray-600">{{ check.reason }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <AiAnalysisFeedbackForm
        :analysable-type="analysableType"
        :analysable-id="analysableId"
        analysis-type="complaints_receipt"
      />
    </div>

    <div v-if="claimActive && (analyzedAt || errorMessage)" class="mt-4 border-t border-gray-100 pt-4">
      <button
        type="button"
        :disabled="retrying"
        class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 focus:outline-hidden disabled:opacity-50"
        @click="retryAnalysis"
      >
        {{ t("retry_button") }}
      </button>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal } from "@estaly/ui";
import { useRetryAnalysis } from "@/composables/useRetryAnalysis";
import {
  verdictLabel,
  badgeClassFor,
  mapExtractedFields,
  mapChecks,
  formatAnalyzedAt,
} from "@/components/claim/details/complaintsReceiptAnalysisFormatter";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";

export default {
  name: "ComplaintsReceiptAnalysisModal",
  components: { Modal, AiAnalysisFeedbackForm },

  setup(props, { emit }) {
    const { t } = useI18n();
    const { retrying, retryAnalysis } = useRetryAnalysis("complaints_receipt");
    return { t, retrying, retryAnalysis: () => retryAnalysis(props.analysableId, emit) };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    analysis: {
      type: Object,
      default: null,
    },
    analyzedAt: {
      type: [String, Date],
      default: null,
    },
    analysableId: {
      type: Number,
      required: true,
    },
    analysableType: {
      type: String,
      required: true,
    },
    claimActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "retry-success"],
  computed: {
    errorMessage() {
      return this.analysis?.error || null;
    },
    verdict() {
      return this.analysis?.overallVerdict || null;
    },
    extracted() {
      return this.analysis?.extracted || null;
    },
    extractedFields() {
      return mapExtractedFields(this.analysis);
    },
    checks() {
      return mapChecks(this.analysis);
    },
    verdictBadgeClass() {
      return badgeClassFor(this.verdict);
    },
    formattedAnalyzedAt() {
      return formatAnalyzedAt(this.analyzedAt);
    },
  },
  methods: {
    verdictLabel,
    badgeClassFor,
  },
};
</script>

<i18n src="./ComplaintsReceiptAnalysisModal.i18n.json"></i18n>
